import { useRef, useCallback, useEffect } from "react";
import { useStore } from "jotai";
import axios from "axios";
import { getApiBaseUrl } from "@/config/api";
import dictationWorkletUrl from "./dictation-audio-worklet.js?url";
import {
  appendDictationHistory,
  dictationFinalizedTranscriptAtom,
  dictationSessionAtom,
  type DictationDeviceState,
  type DictationStage,
} from "@/atoms/dictationSessionAtoms";
import { logger } from "@/utils/logger";

/** Realtime endpoint; the session (model, prompt, audio format) is bound to the client secret. */
export const DICTATION_WS_URL = "wss://api.openai.com/v1/realtime";
// The session's input format: PCM16 mono at 24 kHz (an integer 2:1 decimation of the 48 kHz context).
const DICTATION_SAMPLE_RATE = 24000;

// Spoken dictation commands, rendered as text. "colon" is deliberately absent:
// in a radiology report it is far more often the organ than the punctuation.
const SPOKEN_COMMANDS: Record<string, string> = {
  "new paragraph": "\n\n",
  "new line": "\n",
  period: ".",
  "full stop": ".",
  "question mark": "?",
  "exclamation mark": "!",
  "exclamation point": "!",
  comma: ",",
};
// Private-use marker for "capitalize the next letter"; never present in a transcript.
const SENTENCE_START = "\uE000";
const SPOKEN_COMMAND_RE = new RegExp(
  `\\s*([,.!?]*)\\s*\\b(${Object.keys(SPOKEN_COMMANDS).join("|")})\\b[,.!?]*[ \\t]*`,
  "gi",
);

/**
 * Render spoken commands ("period", "new line", ...) as punctuation and line
 * breaks. The transcriber writes them as words and often adds its own
 * punctuation next to them ("unremarkable. Period new line kidneys"); that
 * punctuation is absorbed so the command alone decides. A word that starts a
 * new sentence or line after a command is capitalized.
 */
export function applyDictationCommands(text: string): string {
  return text
    .replace(SPOKEN_COMMAND_RE, (_match, before: string, command: string) => {
      const rendered = SPOKEN_COMMANDS[command.toLowerCase()];
      if (rendered.startsWith("\n")) return before + rendered + SENTENCE_START;
      return rendered + " " + (rendered === "," ? "" : SENTENCE_START);
    })
    .replace(/\uE000[ \t]*(\p{L})?/gu, (_match, letter?: string) => letter?.toUpperCase() ?? "")
    .replace(/[ \t]+\n/g, "\n")
    .trim();
}

function encodeBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

const getAudioContextResumeErrorMessage = (err: unknown): string => {
  if (!(err instanceof DOMException)) {
    return "Microphone failed to start - try again or reconnect your device.";
  }
  if (err.name === "NotAllowedError") {
    return "Microphone blocked by autoplay policy - click again to retry.";
  }
  if (err.name === "InvalidStateError") {
    return "Audio session lost - try again.";
  }
  return "Microphone failed to start - try again or reconnect your device.";
};

interface UseRealtimeTranscriptionOptions {
  userId: string;
  token: string;
  onFinalTranscript?: (text: string) => void | Promise<void>;
}

/**
 * Hook for real-time audio transcription over an OpenAI Realtime
 * transcription session (the backend mints an ephemeral client secret).
 *
 * Each utterance ends with input_audio_buffer.commit: the transcriber streams
 * deltas while the rad speaks and sends one completed transcript for the
 * committed audio. The socket is then retired and the next one warms while the
 * editor applies the completed utterance; microphone/context resources are reused.
 *
 * All pipeline state is written to dictationSessionAtom (Jotai). Consumers
 * read the atom directly — this hook returns only action functions.
 */
// Persisted user preference so a rad's mic choice survives page reloads.
const MIC_DEVICE_STORAGE_KEY = "dictation:deviceId";

// Bound a missing provider completion without treating partial text as complete.
export const FINALIZE_GIVE_UP_MS = 10000;

export interface MicDevice {
  deviceId: string;
  label: string;
}

/** Margin so a token is not used to open a socket seconds before it expires. */
const DICTATION_TOKEN_EXPIRY_MARGIN_MS = 15_000;

export function hasUsableDictationToken(expiresAt: number, now = Date.now()): boolean {
  return expiresAt - DICTATION_TOKEN_EXPIRY_MARGIN_MS > now;
}

export function useRealtimeTranscription({
  userId,
  token,
  onFinalTranscript,
}: UseRealtimeTranscriptionOptions) {
  // selectedMicIdRef mirrors the persisted device preference. Initialised
  // from localStorage once at mount; updated at every selectMicDevice call
  // and at the fallback-clear path in connect().
  const selectedMicIdRef = useRef<string | null>(
    (() => {
      try {
        return localStorage.getItem(MIC_DEVICE_STORAGE_KEY);
      } catch {
        return null;
      }
    })(),
  );

  // Audio refs
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  // Track whether `addModule` has registered the dictation processor on
  // the current AudioContext. Once registered, subsequent start() calls
  // can build AudioWorkletNodes synchronously without re-loading.
  const workletReadyRef = useRef(false);

  // Live refs that track pipeline state for guards inside callbacks. Atom
  // writes are async (React batching), so a closure-bound atom read in
  // connect()/start() can be stale. Refs are updated synchronously at the
  // same site as every atom write.
  const isReadyRef = useRef(false);
  const isConnectingRef = useRef(false);

  // WebSocket ref
  const wsRef = useRef<WebSocket | null>(null);

  // Callback ref (so WebSocket onmessage always has the latest)
  const onFinalTranscriptRef = useRef(onFinalTranscript);
  onFinalTranscriptRef.current = onFinalTranscript;

  // Ephemeral client secret from the backend
  const tokenRef = useRef<string>("");
  // Epoch ms after which tokenRef can no longer OPEN a socket (open sockets
  // outlive it). The backend mints ephemeral client secrets, never the key.
  const tokenExpiresAtRef = useRef<number>(0);
  // Latest openWebSocket, so the async token refresh inside it can re-enter it.
  const openWebSocketRef = useRef<(() => void) | null>(null);
  // Bumped by disconnect so a token fetch resolving afterwards opens nothing.
  const sessionRef = useRef(0);
  // Shared in-flight promise. Concurrent connect() calls (e.g. StrictMode
  // double-mount, or the user double-clicking Dictate) await the same fetch
  // instead of each minting a new client secret on the server.
  const tokenPromiseRef = useRef<Promise<void> | null>(null);

  // Track consecutive connection failures to back off
  const failCountRef = useRef(0);

  // The identity survives every async step. Diagnostic liveFinals are not a
  // pending delivery queue; an idle socket must never replay them.
  const utteranceRef = useRef<{ phase: "recording" | "finalizing" | "delivering" } | null>(null);
  const finalizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFinalizeTimer = useCallback(() => {
    if (finalizeTimerRef.current !== null) clearTimeout(finalizeTimerRef.current);
    finalizeTimerRef.current = null;
  }, []);

  // Jotai store for atom writes
  const store = useStore();

  const writeStage = useCallback(
    (stage: DictationStage) => {
      store.set(dictationSessionAtom, (s) => ({ ...s, stage }));
    },
    [store],
  );

  const writeError = useCallback(
    (error: string | null) => {
      store.set(dictationSessionAtom, (s) => ({ ...s, error }));
    },
    [store],
  );

  const writeDevice = useCallback(
    (patch: Partial<DictationDeviceState>) => {
      store.set(dictationSessionAtom, (s) => ({
        ...s,
        device: { ...s.device, ...patch },
      }));
    },
    [store],
  );

  const writeInterim = useCallback(
    (text: string | null) => {
      store.set(dictationSessionAtom, (s) => ({
        ...s,
        lastInterim: text ? { text, ts: Date.now() } : null,
      }));
    },
    [store],
  );

  // Append a completed transcript to the live finalized buffer (SSoT
  // for the canonical committed text — what consumers like the editor
  // ghost text and mic test panel read, and what deliverTranscript hands
  // to onFinalTranscript).
  const appendLiveFinal = useCallback(
    (segment: string) => {
      store.set(dictationSessionAtom, (s) => ({ ...s, liveFinals: [...s.liveFinals, segment] }));
    },
    [store],
  );

  const resetLiveFinals = useCallback(() => {
    store.set(dictationSessionAtom, (s) => ({ ...s, liveFinals: [] }));
  }, [store]);

  /**
   * Fetch a client secret from the backend. Concurrent callers share one
   * in-flight promise so we mint exactly one secret per connect cycle.
   */
  const getDictationToken = useCallback(async () => {
    if (tokenPromiseRef.current) return tokenPromiseRef.current;
    const fetchPromise = (async () => {
      const response = await axios.post(
        `${getApiBaseUrl()}/api/dictation_token`,
        { user_id: userId },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } },
      );
      if (
        response.data.status === 200 &&
        typeof response.data.token === "string" &&
        response.data.token &&
        typeof response.data.expires_in === "number" &&
        response.data.expires_in * 1000 > DICTATION_TOKEN_EXPIRY_MARGIN_MS
      ) {
        tokenRef.current = response.data.token;
        tokenExpiresAtRef.current = Date.now() + response.data.expires_in * 1000;
        return;
      }
      throw new Error("Invalid dictation token response");
    })();
    tokenPromiseRef.current = fetchPromise;
    try {
      await fetchPromise;
    } finally {
      tokenPromiseRef.current = null;
    }
  }, [userId, token]);

  const failDelivery = useCallback(
    (message: string) => {
      if (!utteranceRef.current) return;
      utteranceRef.current = null;
      clearFinalizeTimer();
      const session = store.get(dictationSessionAtom);
      const text = [...session.liveFinals, session.lastInterim?.text ?? ""]
        .filter(Boolean)
        .join(" ");
      appendDictationHistory(store, {
        ts: Date.now(),
        text,
        postprocessed: "",
        mode: session.mode,
        dispatch: { kind: "dropped", reason: "delivery" },
      });
      writeError(message + " Open Mic settings to copy the transcript.");
      writeStage(isReadyRef.current ? "ready" : "idle");
    },
    [clearFinalizeTimer, store, writeError, writeStage],
  );

  const deliverTranscript = useCallback(async () => {
    const utterance = utteranceRef.current;
    if (utterance?.phase !== "finalizing") return;
    utterance.phase = "delivering";
    clearFinalizeTimer();
    const finals = store.get(dictationFinalizedTranscriptAtom).trim();
    const transcript = finals || store.get(dictationSessionAtom).lastInterim?.text.trim() || "";
    writeStage("delivering");
    try {
      await onFinalTranscriptRef.current?.(transcript);
    } catch (err) {
      logger.warn("dictation_delivery_failed", err);
      if (utteranceRef.current === utterance) failDelivery("Dictation was not inserted.");
      return;
    }
    if (utteranceRef.current !== utterance) return;
    utteranceRef.current = null;
    writeInterim(null);
    writeStage(isReadyRef.current ? "ready" : "connecting");
  }, [clearFinalizeTimer, failDelivery, store, writeInterim, writeStage]);

  // Raw delta text streamed for the current utterance. The interim shown to
  // the rad is this text with spoken commands applied.
  const deltaTextRef = useRef("");

  /**
   * One socket carries one utterance: deltas stream while recording, and the
   * commit sent by stop() yields exactly one completed transcript (or an
   * empty-buffer error when nothing was captured). Either outcome retires the
   * socket so the next utterance starts on a fresh one.
   */
  const handleMessage = useCallback(
    (evt: MessageEvent, ws: WebSocket) => {
      try {
        const d = JSON.parse(evt.data);
        const phase = utteranceRef.current?.phase;
        if (phase !== "recording" && phase !== "finalizing") return;
        switch (d.type) {
          case "conversation.item.input_audio_transcription.delta":
            deltaTextRef.current += d.delta ?? "";
            writeInterim(applyDictationCommands(deltaTextRef.current));
            return;
          case "conversation.item.input_audio_transcription.completed": {
            const transcript = applyDictationCommands(d.transcript ?? "");
            if (transcript) appendLiveFinal(transcript);
            writeInterim(null);
            void deliverTranscript();
            break;
          }
          case "conversation.item.input_audio_transcription.failed":
            logger.warn("dictation_transcription_failed", d.error);
            failDelivery("Transcription failed.");
            break;
          case "error":
            if (phase !== "finalizing") {
              logger.warn("dictation_realtime_error", d.error);
              return;
            }
            // Committing an empty buffer means nothing was captured: an empty utterance.
            if (d.error?.code === "input_audio_buffer_commit_empty") {
              void deliverTranscript();
            } else {
              logger.warn("dictation_realtime_error", d.error);
              failDelivery("Transcription failed.");
            }
            break;
          default:
            return;
        }
        ws.close(1000);
      } catch (err) {
        logger.warn("dictation_ws_message_parse_failed", err);
      }
    },
    [appendLiveFinal, deliverTranscript, failDelivery, writeInterim],
  );

  /**
   * Retry with backoff (up to 3 attempts). After that, surface an error so the
   * consumer can show a toast — silently giving up leaves the rad pressing
   * Dictate with no feedback.
   */
  const retryOrGiveUp = useCallback(
    (retry: () => void) => {
      // A refresh and a connect can fail on the same token request. Keep one
      // retry chain: a second timer would orphan the first, and disconnect()
      // could no longer cancel it.
      if (reconnectTimerRef.current !== null) return;
      failCountRef.current++;
      if (failCountRef.current < 3) {
        const session = sessionRef.current;
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          if (session === sessionRef.current) retry();
        }, failCountRef.current * 2000);
      } else {
        if (!utteranceRef.current) writeStage("idle");
        writeError("Dictation unavailable — retry in a moment.");
      }
    },
    [writeStage, writeError],
  );

  /**
   * Warm the next socket. Only its active utterance can consume results.
   */
  const openWebSocket = useCallback(() => {
    if (!tokenRef.current || wsRef.current) return;
    if (!hasUsableDictationToken(tokenExpiresAtRef.current)) {
      // Expired short-lived secret: mint a fresh one, then warm the socket.
      tokenRef.current = "";
      const refresh = () => {
        const session = sessionRef.current;
        getDictationToken()
          .then(() => {
            if (session === sessionRef.current) openWebSocketRef.current?.();
          })
          .catch((err) => {
            if (session !== sessionRef.current) return;
            logger.warn("dictation_token_refresh_failed", err);
            retryOrGiveUp(refresh);
          });
      };
      refresh();
      return;
    }
    // A stale HMR ref can hold a non-string value and break the WS constructor.
    if (typeof tokenRef.current !== "string") {
      logger.warn("dictation_tokenRef_polluted type=%s", typeof tokenRef.current);
      tokenRef.current = "";
      return;
    }

    const ws = new WebSocket(DICTATION_WS_URL, [
      "realtime",
      `openai-insecure-api-key.${tokenRef.current}`,
    ]);

    ws.onopen = () => {
      if (wsRef.current !== ws) return;
      isReadyRef.current = true;
      isConnectingRef.current = false;
      if (!utteranceRef.current) writeStage("ready");
      // Keep delivery errors visible after reconnect so words remain recoverable.
      failCountRef.current = 0;
    };

    ws.onmessage = (evt) => {
      if (wsRef.current === ws) handleMessage(evt, ws);
    };

    ws.onerror = () => {
      if (wsRef.current !== ws) return;
      writeError("Dictation connection error");
    };

    ws.onclose = (e) => {
      // A retired socket must not clear its replacement after a mic switch.
      if (wsRef.current !== ws) return;

      wsRef.current = null;
      isReadyRef.current = false;
      isConnectingRef.current = false;

      if (
        utteranceRef.current?.phase === "recording" ||
        utteranceRef.current?.phase === "finalizing"
      ) {
        teardownAudio();
        failDelivery("Dictation ended before transcription completed.");
      }
      if (!utteranceRef.current) writeStage("connecting");
      // A clean close retires one utterance's socket, not the reporter session.
      // Keep this timer cancellable so unmount cannot resurrect a socket.
      if (failCountRef.current < 3 && tokenRef.current) {
        failCountRef.current += 1;
        reconnectTimerRef.current = setTimeout(
          () => {
            reconnectTimerRef.current = null;
            openWebSocket();
          },
          e.code === 1000 ? 0 : 1000,
        );
      } else if (!utteranceRef.current) {
        writeStage("idle");
      }
    };

    wsRef.current = ws;
  }, [handleMessage, failDelivery, writeStage, writeError, getDictationToken, retryOrGiveUp]);
  openWebSocketRef.current = openWebSocket;

  /**
   * Stop the audio pipeline (shared by stop and cancel)
   */
  const teardownAudio = useCallback(() => {
    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null;
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    // AudioContext + registered worklet processor stay alive across
    // start/stop cycles — addModule is per-AudioContext, and tearing
    // the ctx down here would force a re-load every recording. Final
    // close happens in disconnect() / unmount.
  }, []);

  /**
   * Acquire the microphone MediaStream (lazy, called from start()).
   *
   * Privacy: this is the ONLY place a live mic stream is opened, and it
   * runs only on an explicit user-initiated start() — never on warm-up
   * connect(). Reuses an already-open stream across holds within a
   * session (push-to-talk presses are synchronous after the first), so
   * the first word of every utterance isn't clipped by a cold re-acquire.
   *
   * Side effects beyond returning the stream: populates the device
   * picker list + quality/applied-constraint diagnostics, because the
   * permission grant that makes enumerateDevices return labels happens
   * here.
   */
  const acquireMicStream = useCallback(async (): Promise<MediaStream> => {
    if (mediaStreamRef.current) return mediaStreamRef.current;

    // Chrome-managed audio pipeline. All processing on; let the
    // browser do its job. The previous attempts to tune individual
    // constraints (disabling NR, layering software gain) were
    // research-disproven — production dictation apps (Wispr Flow,
    // Discord) leave Chrome's pipeline default-on and surface a
    // warning when the input is HFP-narrowband instead of trying
    // to compensate in JS.
    const baseConstraints: MediaTrackConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: { ideal: 1 },
    };
    const persistedId = selectedMicIdRef.current;
    // `exact` (not `ideal`): Chrome silently falls back to the default
    // device when an `ideal` deviceId can't be bound — which is what
    // happens with AirPods on macOS via the WebRTC Communications-
    // Device path. Picker shows AirPods, persisted preference matches,
    // but the actual MediaStream is built-in. `exact` forces Chrome
    // to honor the picker or throw OverconstrainedError (caught below
    // and falls back to the default constraint set so we don't strand
    // the rad with no working mic).
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: persistedId
          ? { ...baseConstraints, deviceId: { exact: persistedId } }
          : baseConstraints,
      });
    } catch (err) {
      if (
        persistedId &&
        err instanceof DOMException &&
        (err.name === "OverconstrainedError" || err.name === "NotFoundError")
      ) {
        // Persisted device gone (AirPods disconnected, USB unplugged).
        // Drop the preference and fall through to default selection.
        try {
          localStorage.removeItem(MIC_DEVICE_STORAGE_KEY);
        } catch {
          /* localStorage blocked */
        }
        selectedMicIdRef.current = null;
        stream = await navigator.mediaDevices.getUserMedia({ audio: baseConstraints });
      } else {
        throw err;
      }
    }
    mediaStreamRef.current = stream;
    const track = stream.getAudioTracks()[0];
    // Detect HFP narrowband (AirPods + every BT headset's mic-active
    // mode). Phone-call codec maxes out at 8 kHz audio bandwidth no
    // matter what the rad's voice quality is. Trying to compensate
    // in JS amplifies quantization noise without recovering the lost
    // frequency content. The right move is the same one Wispr Flow
    // makes: tell the rad to switch.
    const trackSampleRate = track?.getSettings().sampleRate ?? 0;
    const quality: "narrowband" | "wideband" =
      trackSampleRate > 0 && trackSampleRate < 32000 ? "narrowband" : "wideband";
    writeDevice({ quality });
    const settings = track?.getSettings() ?? {};
    writeDevice({
      appliedConstraints: {
        sampleRate: typeof settings.sampleRate === "number" ? settings.sampleRate : null,
        echoCancellation:
          typeof settings.echoCancellation === "boolean" ? settings.echoCancellation : null,
        noiseSuppression:
          typeof settings.noiseSuppression === "boolean" ? settings.noiseSuppression : null,
        autoGainControl:
          typeof settings.autoGainControl === "boolean" ? settings.autoGainControl : null,
      },
    });

    // Now that the permission grant has happened, enumerateDevices
    // returns labels — populate the picker list. macOS quirk: WebRTC
    // sometimes binds to the "Communications Device" instead of
    // "Sound Input" default, which is why pairing AirPods doesn't
    // automatically route mic capture; the picker lets the rad
    // override that.
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs: MicDevice[] = devices
        .filter((d) => d.kind === "audioinput" && d.deviceId && d.deviceId !== "default")
        .map((d) => ({ deviceId: d.deviceId, label: d.label || "Unknown microphone" }));
      writeDevice({ available: inputs });
      // Sync selectedMicId with what Chrome actually grabbed when
      // we have no persisted preference yet — so the picker UI shows
      // the live device, not "no selection".
      const liveId = stream.getAudioTracks()[0]?.getSettings().deviceId ?? null;
      if (!persistedId && liveId) {
        selectedMicIdRef.current = liveId;
        writeDevice({ selectedId: liveId });
      }
    } catch (err) {
      logger.warn("dictation_enumerate_devices_failed", err);
    }
    return stream;
  }, [writeDevice]);

  /**
   * Connect (warm-up): get token + open WebSocket + create AudioContext +
   * load the audio worklet module. Deliberately does NOT acquire the mic —
   * no MediaStream is opened and no browser mic indicator turns on here.
   * The mic is opened lazily on the first start() (see acquireMicStream).
   * This warm-up removes the socket/context/worklet latency so pressing
   * dictate stays instant while keeping the mic dark until the rad acts.
   */
  const connect = useCallback(async () => {
    // Read guards via refs, not atom. selectMicDevice updates refs synchronously
    // before awaiting connect; without ref reads, this closure would see stale
    // values from the previous render's atom snapshot.
    if (isReadyRef.current || isConnectingRef.current || wsRef.current) return;

    // Reset the auto-retry counter on every user-visible connect attempt.
    // After 3 auto-retry failures the hook surfaces an error toast; without
    // this reset, the user clicking "Enable dictation" hits failCount >= 3
    // immediately, no retry runs, and the toast never clears.
    const currentError = store.get(dictationSessionAtom).error;
    if (currentError) failCountRef.current = 0;

    isConnectingRef.current = true;
    writeStage("connecting");
    writeError(null);
    const session = sessionRef.current;

    try {
      if (!tokenRef.current || !hasUsableDictationToken(tokenExpiresAtRef.current)) {
        await getDictationToken();
        if (session !== sessionRef.current) return;
      }
      openWebSocket();

      // Pre-create AudioContext and load the worklet processor module so
      // start() can be synchronous (the keydown handler can't await).
      // AudioContext is reused across many start/stop cycles. Locked to
      // 48 kHz so the resample-to-24k ratio is always integer 2 — at
      // non-integer ratios the box filter aliases audibly.
      if (!audioCtxRef.current) {
        let ctx: AudioContext;
        try {
          ctx = new AudioContext({ sampleRate: 48000 });
        } catch (err) {
          logger.warn("dictation_audioctx_48k_unavailable", err);
          ctx = new AudioContext();
        }
        try {
          await ctx.audioWorklet.addModule(dictationWorkletUrl);
          workletReadyRef.current = true;
        } catch (err) {
          // If the module fails to load, leave workletReadyRef false; start()
          // will see this and bail. Surface as a hook error so the consumer
          // toast fires instead of "start blocked" silently.
          logger.error("dictation_worklet_addmodule_failed", err);
          ctx.close().catch((closeErr) => {
            logger.warn("dictation_audioctx_close_failed", closeErr);
          });
          throw err;
        }
        audioCtxRef.current = ctx;
      }
      failCountRef.current = 0;
    } catch (err) {
      logger.warn("dictation_connect_failed", err);
      if (session !== sessionRef.current) return;
      isConnectingRef.current = false;
      retryOrGiveUp(() => void connect());
    }
    // Guards read via refs; connect re-creates only when store/write helpers change.
  }, [store, getDictationToken, openWebSocket, writeStage, writeError, retryOrGiveUp]);

  /**
   * Disconnect and cleanup everything
   */
  const disconnect = useCallback(() => {
    sessionRef.current += 1;
    if (utteranceRef.current) failDelivery("Dictation was interrupted before insertion.");
    if (reconnectTimerRef.current !== null) clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = null;
    teardownAudio();
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch((err) => {
        logger.warn("dictation_disconnect_audioctx_close_failed", err);
      });
      audioCtxRef.current = null;
      workletReadyRef.current = false;
    }
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    // Close even if CONNECTING — a WS that opens after disconnect would sit
    // open with no owner. The onclose identity guard above only fires on an
    // actual close event.
    if (wsRef.current) {
      try {
        wsRef.current.close(1000);
      } catch (err) {
        logger.warn("dictation_disconnect_ws_close_failed", err);
      }
    }
    wsRef.current = null;
    isReadyRef.current = false;
    isConnectingRef.current = false;
    resetLiveFinals();
    utteranceRef.current = null;
    clearFinalizeTimer();
    failCountRef.current = 0;
    writeStage("idle");
    writeError(null);
    writeInterim(null);
    writeDevice({ inputLevel: null, appliedConstraints: null });
  }, [
    teardownAudio,
    failDelivery,
    resetLiveFinals,
    clearFinalizeTimer,
    writeStage,
    writeError,
    writeInterim,
    writeDevice,
  ]);

  /**
   * Wire the live audio graph: MediaStreamSource → worklet → resample/encode
   * → WebSocket, resume the context, and flip stage to "listening". Runs only
   * once a MediaStream is present (synchronously when the stream is already
   * warm from a prior hold, or after acquireMicStream resolves on the first
   * hold). `writeStage("listening")` happens HERE — not at the synchronous
   * top of start() — so the live indicator never claims "listening" while the
   * permission prompt is still up or the stream is still resolving.
   */
  const wirePipeline = useCallback(
    (ctx: AudioContext, stream: MediaStream) => {
      const utterance = utteranceRef.current;
      writeStage("listening");

      // Resume in case the browser autoplay policy left the context
      // suspended. Without this, AudioWorkletProcessor.process() never
      // gets called and audio never reaches the WS. Fire-and-forget — by
      // the time the first 128-sample block arrives the resume promise
      // has already settled. A rejection here (autoplay-policy block,
      // suspended after backgrounding, revoked permission, OS audio
      // loss) means audio never reaches the transcriber; tear down the just-
      // created source + worklet node so a retry doesn't stack a second
      // pair onto the same suspended ctx, allow a fresh user gesture, and
      // surface a message that matches the rejection cause.
      ctx.resume().catch((err: unknown) => {
        logger.warn("dictation_audioctx_resume_failed", err);
        if (utteranceRef.current !== utterance) return;
        teardownAudio();
        utteranceRef.current = null;
        writeError(getAudioContextResumeErrorMessage(err));
        // The warm socket remains usable; allow a fresh user gesture to resume audio.
        writeStage(isReadyRef.current ? "ready" : "idle");
      });

      const source = ctx.createMediaStreamSource(stream);
      sourceRef.current = source;
      const node = new AudioWorkletNode(ctx, "dictation-processor");
      workletNodeRef.current = node;

      const ratio = ctx.sampleRate / DICTATION_SAMPLE_RATE;

      // Resample + soft-clip + ship to the transcriber. Box filter averages
      // boxSize source samples per output sample (anti-alias at integer
      // ratios); tanh soft-clip bounds peaks without DynamicsCompressor's
      // attack lag.
      node.port.onmessage = (e) => {
        const msg = e.data as { type?: string };
        if (msg && msg.type === "rms") {
          writeDevice({ inputLevel: (msg as { level: number }).level });
          return;
        }
        const float32 =
          msg && msg.type === "samples"
            ? (msg as { data: Float32Array }).data
            : (e.data as Float32Array);
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        const boxSize = Math.max(1, Math.ceil(ratio));
        const targetLen = Math.max(1, Math.floor(float32.length / ratio));
        const int16 = new Int16Array(targetLen);
        for (let i = 0; i < targetLen; i++) {
          const startIdx = i * ratio;
          let sum = 0;
          let count = 0;
          for (let j = 0; j < boxSize; j++) {
            const sIdx = Math.floor(startIdx + j);
            if (sIdx < float32.length) {
              sum += float32[sIdx];
              count++;
            }
          }
          const sample = count > 0 ? sum / count : 0;
          const s = Math.tanh(sample);
          int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        ws.send(
          JSON.stringify({ type: "input_audio_buffer.append", audio: encodeBase64(int16.buffer) }),
        );
      };

      // source → worklet → destination. The destination connection is
      // not strictly required for AudioWorkletProcessor.process() to
      // fire (unlike ScriptProcessorNode where it was), but it forces
      // the AudioContext to remain in the "running" state — without it,
      // Chrome's autoplay policy can park the context in "suspended"
      // mid-session and audio silently stops flowing. The processor's
      // output buffer is left empty (no writes in process()), so
      // destination receives silence and the rad doesn't hear playback.
      source.connect(node);
      node.connect(ctx.destination);
    },
    [writeStage, writeDevice, writeError, teardownAudio],
  );

  /**
   * Start recording — begin streaming audio. WebSocket/AudioContext/worklet
   * are already warm from connect(); the mic MediaStream is acquired HERE
   * (lazily, on this explicit user-initiated press) if not already open from
   * a prior hold in the same session.
   *
   * Returns true if the start was *initiated*, false if a warm-up
   * precondition (not connected, context/worklet not ready) or re-entrancy
   * (already recording) blocked it. Note the boolean now means "starting",
   * not "capture is live": on the first hold the mic is acquired
   * asynchronously, so capture begins a tick later when the stream resolves.
   * If that async acquire fails, the hook drives its own reset (writeError +
   * retire the utterance) — the caller already saw `true` and
   * won't run its own !started cleanup.
   */
  const start = useCallback((): boolean => {
    // Read live values from refs so a render-boundary race after a mic
    // switch (selectMicDevice → connect → ws onopen → atom write) doesn't
    // make start see an outdated isReady=false right after a successful
    // re-connect. The mic stream is intentionally NOT a precondition here:
    // a null stream is the trigger to acquire it (first hold), not a block.
    const ctx = audioCtxRef.current;
    if (
      !isReadyRef.current ||
      utteranceRef.current ||
      !wsRef.current ||
      !ctx ||
      !workletReadyRef.current
    ) {
      return false;
    }

    resetLiveFinals();
    deltaTextRef.current = "";
    writeInterim(null);
    writeError(null);
    const utterance = { phase: "recording" as const };
    utteranceRef.current = utterance;
    // The utterance is the start intent, including while mic permission is pending.

    // Fast path: stream already open from a prior hold this session — wire
    // synchronously so push-to-talk presses never clip the first word.
    if (mediaStreamRef.current) {
      wirePipeline(ctx, mediaStreamRef.current);
      return true;
    }

    // First hold this session: acquire the mic lazily, then wire — unless
    // the rad released/cancelled before the stream resolved.
    void (async () => {
      let stream: MediaStream;
      try {
        stream = await acquireMicStream();
      } catch (err) {
        logger.warn("dictation_mic_acquire_failed", err);
        // No silent fallback: surface the failure and reset. The caller saw
        // start()=true, so the hook owns this reset.
        if (utteranceRef.current !== utterance) return;
        utteranceRef.current = null;
        mediaStreamRef.current = null;
        const errName = err instanceof DOMException ? err.name : null;
        const msg =
          errName === "NotAllowedError"
            ? "Microphone permission denied — allow mic access and try again."
            : "Microphone unavailable — check device permissions and try again.";
        writeError(msg);
        writeStage("ready");
        return;
      }
      // Re-check intent: a keyup/cancel between the press and the stream
      // resolving means the rad is no longer holding — going live now would
      // open the mic with no active recording (a privacy leak in a privacy
      // fix). Stop the freshly-acquired tracks and bail without wiring.
      if (utteranceRef.current !== utterance || utterance.phase !== "recording") {
        stream.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
        writeDevice({ inputLevel: null });
        return;
      }
      // Context could have been torn down (disconnect/selectMicDevice)
      // while we awaited — re-read it rather than trusting the closed-over
      // ctx, and bail if it's gone.
      const liveCtx = audioCtxRef.current;
      if (!liveCtx) {
        utteranceRef.current = null;
        return;
      }
      wirePipeline(liveCtx, stream);
    })();
    return true;
    // Guard reads via refs; start() doesn't need to re-create when atom
    // changes, so consumer keydown effects don't churn on every flip.
  }, [
    resetLiveFinals,
    acquireMicStream,
    wirePipeline,
    writeError,
    writeInterim,
    writeStage,
    writeDevice,
  ]);

  /**
   * Committing the audio buffer supplies an unambiguous end: the completed
   * transcript for everything sent. Streamed deltas are not proof that the
   * utterance is complete, and later deltas may revise earlier ones.
   */
  const stop = useCallback(() => {
    const utterance = utteranceRef.current;
    if (utterance?.phase !== "recording") return;
    teardownAudio();
    utterance.phase = "finalizing";
    isReadyRef.current = false;
    writeStage("finalizing");
    const ws = wsRef.current;
    if (ws?.readyState !== WebSocket.OPEN) {
      failDelivery("Dictation connection was lost.");
      return;
    }
    ws.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
    finalizeTimerRef.current = setTimeout(() => {
      if (utteranceRef.current !== utterance || utterance.phase !== "finalizing") return;
      failDelivery("Transcription did not finish.");
      ws.close(1000);
    }, FINALIZE_GIVE_UP_MS);
  }, [teardownAudio, failDelivery, writeStage]);

  /**
   * Cancel recording and retire its socket so late results cannot enter a new utterance.
   */
  const cancel = useCallback(() => {
    if (utteranceRef.current?.phase !== "recording") return;

    teardownAudio();
    resetLiveFinals();
    deltaTextRef.current = "";
    utteranceRef.current = null;
    clearFinalizeTimer();
    writeStage("connecting");
    writeInterim(null);
    isReadyRef.current = false;
    wsRef.current?.close(1000);
  }, [teardownAudio, clearFinalizeTimer, resetLiveFinals, writeStage, writeInterim]);

  // Cleanup on unmount
  useEffect(() => {
    return () => disconnect();
  }, [disconnect]);

  // Switch the active microphone. Stops the current MediaStream + audio
  // pipeline, records the new deviceId as the desired selection, and
  // re-warms the WS via connect(). The new device is bound on the next
  // start() (acquireMicStream reads selectedMicIdRef) — connect() no
  // longer opens the mic, so no stream goes live here. Persists the
  // choice so subsequent sessions skip the macOS default-device quirk.
  const selectMicDevice = useCallback(
    async (deviceId: string) => {
      if (utteranceRef.current && utteranceRef.current.phase !== "recording") return;
      const wasListening = utteranceRef.current?.phase === "recording";
      if (wasListening) {
        const interimText = store.get(dictationSessionAtom).lastInterim?.text ?? "";
        appendDictationHistory(store, {
          ts: Date.now(),
          text: interimText,
          postprocessed: "",
          mode: store.get(dictationSessionAtom).mode,
          dispatch: { kind: "dropped", reason: "device_switch" },
        });
      }
      try {
        localStorage.setItem(MIC_DEVICE_STORAGE_KEY, deviceId);
      } catch {
        /* localStorage blocked → still apply for this session */
      }
      utteranceRef.current = null;
      clearFinalizeTimer();
      selectedMicIdRef.current = deviceId;
      writeDevice({ selectedId: deviceId });
      // Retire the audio pipeline and socket before binding the new device.
      teardownAudio();
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
      // Drop the existing WS too — it's tied to the old token/state and
      // simpler to re-open than to splice a new stream into it mid-flight.
      if (wsRef.current) {
        try {
          wsRef.current.close(1000);
        } catch {
          /* already closed */
        }
        wsRef.current = null;
      }
      // Update refs synchronously so connect()'s guard sees the new
      // values *now*, not after the next render cycle.
      isReadyRef.current = false;
      isConnectingRef.current = false;
      // Force a fresh dictation token on the new connection. The session
      // has been live long enough that the previous token may have
      // expired, and an expired-token WS reconnect closes immediately
      // before producing any results.
      tokenRef.current = "";
      // Reflect the connecting state in UI immediately so the rad sees
      // "Connecting..." rather than a brief "Enable dictation" flash
      // between teardown and the new mic coming online.
      writeStage("connecting");
      await connect();
    },
    [connect, teardownAudio, store, writeDevice, writeStage],
  );

  return {
    connect,
    disconnect,
    start,
    stop,
    cancel,
    selectMicDevice,
  };
}
